/**
 * Hook de l’étape 2 admin : chargement des références, sélection contrôlée,
 * conflits de période, soumission POST/PATCH avec `boosterReferences`.
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getApiBoostersIdGetQueryKey,
  useApiAdminboostersIdPatch,
  useApiAdminboostersPost
} from '@r/api/generated/client.ts';
import type { ReferenceReferenceReadModel } from '@r/api/generated/client.schemas.ts';
import type { BoosterReferenceBoosterWriteModel } from '@r/api/generated/client.schemas.ts';
import type { DataTableFilterField } from '@r/types/index.ts';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { getOrvalErrorMessage } from '@r/api/orval-error-helper.ts';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { toUtcEndOfDay, toUtcStartOfDay } from '@r/utils/date-utils.ts';
import { useAllReferences } from '@r/hooks/use-all-references.ts';
import {
  type BoosterDraftData,
  toBoosterPatchModel,
  toBoosterWriteModel
} from './create-booster-form-schema.ts';
import { invalidateBoostersListQueries } from './use-boosters-collection.ts';
import {
  type BoosterReferenceTier,
  formatBoosterTiersCoefficientSummary,
  getReferenceTiers
} from './booster-reference-tiers.ts';
import {
  type BoosterReferencesSelectionState,
  pruneSelectedReferenceIds,
  selectedReferenceIdsToSet
} from './booster-references-selection.ts';
import { useBoosterReferencesTableColumns } from './use-booster-references-table-columns.tsx';
import {
  formatBoosterConflictDate,
  getBoosterReferenceConflictHint
} from './booster-reference-conflict.ts';

const ALL_REFERENCES_PARAMS = { 'order[brand]': 'asc' as const };

interface UseSelectBoosterReferencesStepParams {
  draft: BoosterDraftData;
  onSuccess: () => void;
  referencesSelection: BoosterReferencesSelectionState;
  onReferencesSelectionChange: (state: BoosterReferencesSelectionState) => void;
  mode?: 'create' | 'edit';
  boosterId?: number;
  originalDraft?: BoosterDraftData;
  pinnedReferences: ReferenceReferenceReadModel[];
  originalBoosterReferences: BoosterReferenceBoosterWriteModel[];
}

/** Orchestration data + actions pour `SelectBoosterReferencesStep` (UI dans un composant séparé). */
export function useSelectBoosterReferencesStep({
  draft,
  onSuccess,
  referencesSelection,
  onReferencesSelectionChange,
  mode = 'create',
  boosterId,
  originalDraft,
  pinnedReferences,
  originalBoosterReferences
}: UseSelectBoosterReferencesStepParams) {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const isEditMode = mode === 'edit';
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [coefficientDialogReference, setCoefficientDialogReference] =
    useState<ReferenceReferenceReadModel | null>(null);

  const pinnedReferenceIds = useMemo(
    () => pinnedReferences.map((reference) => reference.id),
    [pinnedReferences]
  );

  const availableReferencesParams = useMemo(
    () => ({
      'order[brand]': 'asc' as const,
      'available[startDate]': toUtcStartOfDay(draft.startDate).toISOString(),
      'available[endDate]': toUtcEndOfDay(draft.endDate).toISOString()
    }),
    [draft.startDate, draft.endDate]
  );

  const allReferencesQuery = useAllReferences(ALL_REFERENCES_PARAMS);
  const availableReferencesQuery = useAllReferences(availableReferencesParams);
  useOrvalQueryErrorToast(allReferencesQuery, t('toast.error.label'));
  useOrvalQueryErrorToast(availableReferencesQuery, t('toast.error.label'));

  const availableReferences = useMemo(
    () => availableReferencesQuery.data ?? [],
    [availableReferencesQuery.data]
  );

  const selectableReferenceIds = useMemo(() => {
    const ids = new Set(availableReferences.map((reference) => reference.id));

    for (const referenceId of pinnedReferenceIds) {
      ids.add(referenceId);
    }

    return ids;
  }, [availableReferences, pinnedReferenceIds]);

  const selectedReferenceIds = useMemo(
    () =>
      selectedReferenceIdsToSet(
        pruneSelectedReferenceIds(referencesSelection.selectedReferenceIds, selectableReferenceIds)
      ),
    [referencesSelection.selectedReferenceIds, selectableReferenceIds]
  );

  const references = useMemo<ReferenceReferenceReadModel[]>(() => {
    return [...(allReferencesQuery.data ?? [])].sort((left, right) =>
      left.brand.localeCompare(right.brand)
    );
  }, [allReferencesQuery.data]);

  const isReferenceSelectable = useCallback(
    (referenceId: number) => selectableReferenceIds.has(referenceId),
    [selectableReferenceIds]
  );

  const displayedReferences = useMemo(() => {
    if (!showOnlySelected) return references;
    return references.filter((reference) => selectedReferenceIds.has(reference.id));
  }, [references, selectedReferenceIds, showOnlySelected]);

  const updateReferencesSelection = useCallback(
    (nextSelection: BoosterReferencesSelectionState) => {
      onReferencesSelectionChange({
        selectedReferenceIds: pruneSelectedReferenceIds(
          nextSelection.selectedReferenceIds,
          selectableReferenceIds
        ),
        customTiersByReferenceId: nextSelection.customTiersByReferenceId
      });
    },
    [onReferencesSelectionChange, selectableReferenceIds]
  );

  const toggleReference = useCallback(
    (referenceId: number, checked: boolean) => {
      if (checked && !selectableReferenceIds.has(referenceId)) {
        return;
      }

      const nextSelectedReferenceIds = new Set(referencesSelection.selectedReferenceIds);
      const nextCustomTiers = { ...referencesSelection.customTiersByReferenceId };

      if (checked) {
        nextSelectedReferenceIds.add(referenceId);
      } else {
        nextSelectedReferenceIds.delete(referenceId);
        delete nextCustomTiers[referenceId];
      }

      updateReferencesSelection({
        selectedReferenceIds: Array.from(nextSelectedReferenceIds),
        customTiersByReferenceId: nextCustomTiers
      });
    },
    [referencesSelection, selectableReferenceIds, updateReferencesSelection]
  );

  const handleSelectAll = useCallback(() => {
    const nextSelectedReferenceIds = new Set<number>();

    for (const reference of references) {
      if (isReferenceSelectable(reference.id)) {
        nextSelectedReferenceIds.add(reference.id);
      }
    }

    updateReferencesSelection({
      ...referencesSelection,
      selectedReferenceIds: Array.from(nextSelectedReferenceIds)
    });
  }, [isReferenceSelectable, references, referencesSelection, updateReferencesSelection]);

  const getConflictHint = useCallback(
    (reference: ReferenceReferenceReadModel) => getBoosterReferenceConflictHint(reference, t),
    [t]
  );

  const columns = useBoosterReferencesTableColumns({
    draft,
    selectedReferenceIds,
    customTiersByReferenceId: referencesSelection.customTiersByReferenceId,
    isReferenceSelectable,
    getConflictHint,
    onToggleReference: toggleReference,
    onConfigureReference: setCoefficientDialogReference
  });

  const filterFields = useMemo<DataTableFilterField<ReferenceReferenceReadModel>[]>(
    () => [
      { label: t('label.product.brand'), value: 'brand' },
      { label: t('label.product.range'), value: 'productLine' },
      { label: t('label.product.name'), value: 'name' },
      { label: t('label.product.reference'), value: 'uniqueCode' },
      { label: t('label.product.value'), value: 'pointValue' }
    ],
    [t]
  );

  const { table } = useDataTable({
    data: displayedReferences,
    columns,
    filterFields,
    enableAdvancedFilter: true,
    manualPaginate: false,
    manualFilter: false,
    manualSort: false
  });

  const createBoosterMutation = useApiAdminboostersPost({
    mutation: {
      onSuccess: () => {
        toast.success(t('boosters.create.success'));
        invalidateBoostersListQueries(queryClient);
        onSuccess();
      },
      onError: (error) => {
        showOrvalErrorToast(error, getOrvalErrorMessage(error, t('boosters.create.error')));
      }
    }
  });

  const updateBoosterMutation = useApiAdminboostersIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('boosters.edit.success'));
        invalidateBoostersListQueries(queryClient);
        if (boosterId != null) {
          queryClient.invalidateQueries({
            queryKey: getApiBoostersIdGetQueryKey(String(boosterId))
          });
        }
        onSuccess();
      },
      onError: (error) => {
        showOrvalErrorToast(error, getOrvalErrorMessage(error, t('boosters.edit.error')));
      }
    }
  });

  const handleSubmit = useCallback(() => {
    const selectedReferenceIdsList = Array.from(selectedReferenceIds);

    if (selectedReferenceIdsList.length === 0) {
      toast.error(t('boosters.create.references.required'));
      return;
    }

    const conflictedSelection = references.find(
      (reference) => selectedReferenceIds.has(reference.id) && !isReferenceSelectable(reference.id)
    );

    if (conflictedSelection) {
      const activeBooster = conflictedSelection.activeBooster;

      toast.error(
        activeBooster
          ? t('boosters.create.references.conflict', {
              reference: conflictedSelection.uniqueCode,
              startDate: formatBoosterConflictDate(activeBooster.startDate),
              endDate: formatBoosterConflictDate(activeBooster.endDate),
              coefficient:
                formatBoosterTiersCoefficientSummary(conflictedSelection.activeBoosterTiers) ??
                t('boosters.create.references.conflict.coefficient.unknown')
            })
          : t('boosters.create.references.conflict.generic', {
              reference: conflictedSelection.uniqueCode
            })
      );
      return;
    }

    if (isEditMode) {
      if (!originalDraft || boosterId == null) {
        return;
      }

      const patchData = toBoosterPatchModel(
        originalDraft,
        draft,
        originalBoosterReferences,
        selectedReferenceIdsList,
        referencesSelection.customTiersByReferenceId,
        false
      );

      if (Object.keys(patchData).length === 0) {
        toast.info(t('boosters.edit.no.changes'));
        onSuccess();
        return;
      }

      updateBoosterMutation.mutate({ id: String(boosterId), data: patchData });
      return;
    }

    createBoosterMutation.mutate({
      data: toBoosterWriteModel(
        draft,
        selectedReferenceIdsList,
        referencesSelection.customTiersByReferenceId
      )
    });
  }, [
    boosterId,
    createBoosterMutation,
    draft,
    isEditMode,
    isReferenceSelectable,
    onSuccess,
    originalBoosterReferences,
    originalDraft,
    references,
    referencesSelection.customTiersByReferenceId,
    selectedReferenceIds,
    t,
    updateBoosterMutation
  ]);

  const handleSaveReferenceTiers = useCallback(
    (tiers: BoosterReferenceTier[] | undefined) => {
      if (!coefficientDialogReference) {
        return;
      }

      const nextCustomTiers = { ...referencesSelection.customTiersByReferenceId };

      if (!tiers) {
        delete nextCustomTiers[coefficientDialogReference.id];
      } else {
        nextCustomTiers[coefficientDialogReference.id] = tiers;
      }

      updateReferencesSelection({
        ...referencesSelection,
        customTiersByReferenceId: nextCustomTiers
      });
    },
    [coefficientDialogReference, referencesSelection, updateReferencesSelection]
  );

  const coefficientDialogInitialTiers = coefficientDialogReference
    ? getReferenceTiers(
        coefficientDialogReference.id,
        referencesSelection.customTiersByReferenceId,
        draft.coefficient
      )
    : [];

  return {
    isEditMode,
    showOnlySelected,
    setShowOnlySelected,
    coefficientDialogReference,
    setCoefficientDialogReference,
    selectedReferenceIds,
    references,
    table,
    filterFields,
    handleSelectAll,
    handleSubmit,
    handleSaveReferenceTiers,
    coefficientDialogInitialTiers,
    isSubmitting: createBoosterMutation.isPending || updateBoosterMutation.isPending,
    isLoading: allReferencesQuery.isLoading || availableReferencesQuery.isLoading,
    hasSelectableReferences: references.some((reference) => isReferenceSelectable(reference.id))
  };
}
